import AppsIcon from '@atlaskit/icon/core/apps';
import HomeIcon from '@atlaskit/icon/core/home';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import FileIcon from '@atlaskit/icon/core/file';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import LogOutIcon from '@atlaskit/icon/core/log-out';
import { SideNavContent, SideNavFooter, SideNavHeader } from '@atlaskit/navigation-system';
import { Stack, Text } from '@atlaskit/primitives';
import { FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '@app/providers/ThemeProvider';
import { ButtonMenuItem } from '@atlaskit/side-nav-items/button-menu-item';
import {
  ExpandableMenuItem,
  ExpandableMenuItemContent,
  ExpandableMenuItemTrigger
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '@shared/store/hooks';
import { canAccessPage } from '@shared/lib/access';

interface SidebarNavProps {
  onLogout: () => void;
}

const navItemIconProps = { color: 'currentColor' as const, label: '', size: 'small' as const };

export function SidebarNav({ onLogout }: SidebarNavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {userType} = useAppSelector((state) => state.exchange);
  const { theme, toggleTheme } = useTheme();

  const goTo = (path: string) => {
    navigate(path);
  };

  return (
    <>
      <SideNavHeader>
        <Stack space="space.200">
          <div className="side-nav-header-copy">
            <Text weight="bold">Exchange Console</Text>
            <Text size="small">Reporting, billing, and account controls in one navigation rail.</Text>
          </div>
        </Stack>
      </SideNavHeader>

      <SideNavContent>
        <MenuSection>
          <MenuSectionHeading>Workspace</MenuSectionHeading>
          <MenuList>
            {canAccessPage(userType, 'home') ? <ButtonMenuItem
              elemBefore={<HomeIcon {...navItemIconProps} />}
              isSelected={pathname === '/home'}
              onClick={() => goTo('/home')}
            >
              Home
            </ButtonMenuItem>
              : null}

            {/* <ButtonMenuItem elemBefore={<CreditCardIcon {...navItemIconProps} />}>
              Reports
            </ButtonMenuItem> */}

            {canAccessPage(userType, 'reports') ? (
              <ButtonMenuItem
                elemBefore={<FileIcon {...navItemIconProps} />}
                isSelected={pathname === '/reports'}
                onClick={() => goTo('/reports')}
              >
                Reports
              </ButtonMenuItem>
            ) : null}

            {canAccessPage(userType, 'userManagement') ? (
              <ButtonMenuItem
                elemBefore={<PeopleGroupIcon {...navItemIconProps} />}
                isSelected={pathname === '/user-management'}
                onClick={() => goTo('/user-management')}
              >
                User Management
              </ButtonMenuItem>
            ) : null}

            {canAccessPage(userType, 'partnerManagement') ?
              <ExpandableMenuItem>
                <ExpandableMenuItemTrigger>
                  Partner Management
                </ExpandableMenuItemTrigger>
                <ExpandableMenuItemContent>
                  <MenuSection>
                    <MenuList>
                      <ButtonMenuItem
                        elemBefore={<AppsIcon {...navItemIconProps} />}
                        isSelected={pathname === '/supply-partners'}
                        onClick={() => goTo('/supply-partners')}
                      >
                        Supply Partners
                      </ButtonMenuItem>
                      <ButtonMenuItem
                        elemBefore={<LinkExternalIcon {...navItemIconProps} />}
                        isSelected={pathname === '/demand-partners'}
                        onClick={() => goTo('/demand-partners')}
                      >
                        Demand Partners
                      </ButtonMenuItem>
                    </MenuList>
                  </MenuSection>
                </ExpandableMenuItemContent>
              </ExpandableMenuItem> : null}

            {/* <ExpandableMenuItem>
              <ExpandableMenuItemTrigger>
                Payment & Invoice
              </ExpandableMenuItemTrigger>
              <ExpandableMenuItemContent>
                <MenuSection>
                  <MenuList>
                    <ButtonMenuItem
                      elemBefore={<ChartBarIcon {...navItemIconProps} />}
                      isSelected={pathname === '/dashboard'}
                      onClick={() => goTo('/dashboard')}
                    >
                      Invoice
                    </ButtonMenuItem>
                    <ButtonMenuItem elemBefore={<CashIcon {...navItemIconProps} />}>
                      Company & Bank Details
                    </ButtonMenuItem>
                  </MenuList>
                </MenuSection>
              </ExpandableMenuItemContent>
            </ExpandableMenuItem> */}

          </MenuList>
        </MenuSection>
      </SideNavContent>

      <SideNavFooter>
        <MenuSection>
          <MenuList>
            <ButtonMenuItem
              elemBefore={
                theme === 'dark' ? <FiMoon size={14} /> : <FiSun size={14} />
              }
              onClick={toggleTheme}
            >
              {theme === 'dark' ? 'Dark' : 'Light'}
            </ButtonMenuItem>
            <ButtonMenuItem
              elemBefore={<PersonAvatarIcon {...navItemIconProps} />}
              isSelected={pathname === '/dashboard'}
              onClick={() => goTo('/dashboard')}
            >
              User Profile
            </ButtonMenuItem>
            <ButtonMenuItem elemBefore={<LogOutIcon {...navItemIconProps} />} onClick={onLogout}>
              Logout
            </ButtonMenuItem>
          </MenuList>
        </MenuSection>
      </SideNavFooter>
    </>
  );
}
